import { Intent } from "@blueprintjs/core";
import { call, put, select, take, takeLatest } from "redux-saga/effects";
import { saveAs } from "file-saver";
import BackendClient from "api/backend-client";
import { CommonApiMethods } from "api/api-methods";
import { errorHandler } from "api/api-error";
import Toaster, { toastIcons } from "components/Page/components/Toaster";
import logger from "utils/logger";
import { activateLicenseKeyFailure, activateLicenseKeySuccessful, deactivateLicenseKeyFailure, deactivateLicenseKeySuccessful, generateCapabilityRequestFileFailure, generateCapabilityRequestFileSuccessful, LicensingActionType, loadLicensesListFailure, loadLicensesListSuccessful, setSeats, changeSeatToastVisibleStatus, uploadActivationLicenseFileFailure, uploadActivationLicenseFileSuccessful, uploadDeactivationLicenseFileFailure, uploadDeactivationLicenseFileSuccessful, } from "./licensing.actions";
import { LicensingModalType, SeatsToastVisibleStatusType } from "./licensing.types";
import { waitForLogin } from "../auth/auth.saga";
import { licensingModalTypeSelector, licensingSerialNumberSelector } from "./licensing.selectors";
import { apiErrorHandler } from "store/global/global.saga";
const LICENSE_CAPABILITY_REQUEST_FILE_NAME = "ProcessInsightsOfflineActivation.bin";
const LICENSE_DEACTIVATION_CAPABILITY_REQUEST_FILE_NAME = "ProcessInsightsOfflineDeactivation.bin";
const LICENSE_DEACTIVATION_CONFIRMATION_FILE_NAME = "ProcessInsightsOfflineDeactivationConfirmation.bin";
export function* loadLicensesListSaga() {
    try {
        yield call(waitForLogin);
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, CommonApiMethods.GetLicenses]);
        yield put(loadLicensesListSuccessful(data));
    }
    catch (e) {
        yield call(apiErrorHandler, e, "licenses load failed");
        yield put(loadLicensesListFailure());
        logger.error("licenses load failed");
        errorHandler(e, true);
    }
}
function* uploadActivationLicenseFileSaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        yield call([backendClient, CommonApiMethods.UploadActivationLicenseFile], payload);
        yield call(loadLicensesListSaga);
        yield put(uploadActivationLicenseFileSuccessful());
        Toaster().show({
            message: "License activated.",
            intent: Intent.SUCCESS,
            icon: toastIcons.info,
        });
    }
    catch (e) {
        yield call(apiErrorHandler, e, "license file upload failed");
        yield put(uploadActivationLicenseFileFailure(e));
    }
}
function* uploadDeactivationLicenseFileSaga({ payload: { file, key }, }) {
    try {
        const backendClient = BackendClient.getInstance();
        const data = yield call([backendClient, CommonApiMethods.UploadDeactivationLicenseFile], file, key);
        if (!data) {
            throw new Error();
        }
        const fileToSave = new Blob([data], { type: "bin" });
        saveAs(fileToSave, LICENSE_DEACTIVATION_CONFIRMATION_FILE_NAME);
        yield call(loadLicensesListSaga);
        yield put(uploadDeactivationLicenseFileSuccessful());
    }
    catch (e) {
        yield call(apiErrorHandler, e, "license deactivation failed");
        const error = e;
        if (!e.status) {
            error.data.errors[0].text = "An error occurred. The file could not be read.";
        }
        yield put(uploadDeactivationLicenseFileFailure(error));
    }
}
function* generateCapabilityRequestFileSaga({ payload }) {
    const type = yield select(licensingModalTypeSelector);
    const isActivation = type === LicensingModalType.ACTIVATION;
    try {
        const backendClient = BackendClient.getInstance();
        const data = yield call([
            backendClient,
            isActivation
                ? CommonApiMethods.GenerateCapabilityRequestFile
                : CommonApiMethods.GenerateDeactivateCapabilityRequestFile,
        ], payload);
        if (!data) {
            throw new Error();
        }
        const fileToSave = new Blob([data], { type: "bin" });
        saveAs(fileToSave, isActivation ? LICENSE_CAPABILITY_REQUEST_FILE_NAME : LICENSE_DEACTIVATION_CAPABILITY_REQUEST_FILE_NAME);
        yield put(generateCapabilityRequestFileSuccessful());
    }
    catch (e) {
        yield call(apiErrorHandler, e, `generate capability ${!isActivation ? "deactivation" : ""} request file failed`);
        yield put(generateCapabilityRequestFileFailure(e));
    }
}
function* activateLicenseKeySaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        yield call([backendClient, CommonApiMethods.ActivateLicenseKey], payload);
        yield call(loadLicensesListSaga);
        yield put(activateLicenseKeySuccessful());
        Toaster().show({
            message: "License activated.",
            intent: Intent.SUCCESS,
            icon: toastIcons.info,
        });
    }
    catch (e) {
        yield call(apiErrorHandler, e, "license key activation failed");
        yield put(activateLicenseKeyFailure(e));
    }
}
function* deactivateLicenseKeySaga({ payload }) {
    try {
        const backendClient = BackendClient.getInstance();
        yield call([backendClient, CommonApiMethods.DeactivateLicenseKey], payload);
        yield call(loadLicensesListSaga);
        yield put(deactivateLicenseKeySuccessful());
        Toaster().show({
            message: "License deactivated.",
            intent: Intent.SUCCESS,
            icon: toastIcons.info,
        });
    }
    catch (e) {
        yield call(apiErrorHandler, e, "license key deactivation failed");
        yield put(deactivateLicenseKeyFailure(e));
    }
}
export function* changeSeatsSaga({ payload }) {
    try {
        yield call(waitForLogin);
        yield put(setSeats(payload.seats));
        yield put(changeSeatToastVisibleStatus(payload.occupied ? SeatsToastVisibleStatusType.OCCUPIED : SeatsToastVisibleStatusType.RELEASED));
        yield put(changeSeatToastVisibleStatus(false));
    }
    catch (e) {
        logger.error("change seats information is failed");
    }
}
export function* waitForLicensingData() {
    const serialNumber = yield select(licensingSerialNumberSelector);
    if (!serialNumber) {
        yield take(LicensingActionType.LOAD_LICENSES_LIST_SUCCESSFUL);
    }
}
export default function* licensesSaga() {
    yield takeLatest(LicensingActionType.LOAD_LICENSES_LIST, loadLicensesListSaga);
    yield takeLatest(LicensingActionType.UPLOAD_ACTIVATION_LICENSE_FILE, uploadActivationLicenseFileSaga);
    yield takeLatest(LicensingActionType.UPLOAD_DEACTIVATION_LICENSE_FILE, uploadDeactivationLicenseFileSaga);
    yield takeLatest(LicensingActionType.ACTIVATE_LICENSE_KEY, activateLicenseKeySaga);
    yield takeLatest(LicensingActionType.DEACTIVATE_LICENSE_KEY, deactivateLicenseKeySaga);
    yield takeLatest(LicensingActionType.GENERATE_CAPABILITY_REQUEST_FILE, generateCapabilityRequestFileSaga);
    yield takeLatest(LicensingActionType.CHANGE_SEATS, changeSeatsSaga);
}
